import { Gamepad, InputMode } from '../input/Gamepad';
import { saveConfig, defaultConfig, AxisBinding, ButtonBinding, MinimalPad } from '../input/GamepadConfig';

type AxisCtrl = 'aileron' | 'elevator' | 'rudder' | 'throttle' | 'brakeLeft' | 'brakeRight';
type BtnCtrl = 'flapsDown' | 'flapsUp' | 'trimDown' | 'trimUp' | 'brakes' | 'gear';

// Axis controls that default to unbound (null) and so get a "None" button
// alongside "Bind" — the primary flight controls are always bound to
// something, but throttle and the per-wheel brake pedals are optional.
const NULLABLE_AXIS_CTRLS = new Set<AxisCtrl>(['throttle', 'brakeLeft', 'brakeRight']);

const AXIS_CTRLS: { key: AxisCtrl; label: string }[] = [
  { key: 'aileron', label: 'Aileron (roll)' },
  { key: 'elevator', label: 'Elevator (pitch)' },
  { key: 'rudder', label: 'Rudder (yaw)' },
  { key: 'throttle', label: 'Throttle' },
  { key: 'brakeLeft', label: 'Brake (left wheel)' },
  { key: 'brakeRight', label: 'Brake (right wheel)' },
];
const BTN_CTRLS: { key: BtnCtrl; label: string }[] = [
  { key: 'flapsDown', label: 'Flaps down' },
  { key: 'flapsUp', label: 'Flaps up' },
  { key: 'trimDown', label: 'Trim down' },
  { key: 'trimUp', label: 'Trim up' },
  { key: 'brakes', label: 'Brakes' },
  { key: 'gear', label: 'Gear toggle' },
];

/**
 * DOM overlay for configuring the joystick binding (opened with Ctrl+J).
 * Shows every connected USB controller (not just the first) and lets the
 * user rebind axes/buttons — from ANY connected device — by moving/pressing
 * them, toggle per-axis inversion, and set the deadzone. Persists on close.
 * While open, the pad is suspended so binding movements don't fly the aircraft.
 *
 * The "live inputs" panel is scoped to ONE selected device at a time (picked
 * from a dropdown) rather than stacking every device's axes in one long list:
 * a device with only buttons and no axes (e.g. a button box/MFD) previously
 * had nothing at all to show in an axes-only list, making it look like it
 * wasn't detected even though it was — this shows its button presses too.
 */
export class JoystickConfigUI {
  private root: HTMLDivElement;
  private open = false;
  private raf = 0;

  // Binding-capture state
  private listening: { kind: 'axis'; ctrl: AxisCtrl } | { kind: 'button'; ctrl: BtnCtrl } | null = null;
  // Baseline axis values per pad index, so capture measures the DELTA from
  // wherever each stick happened to be resting, not its absolute position.
  private axisBaseline: number[][] = [];
  // Baseline button-pressed state per pad index, captured the moment "Bind"
  // is clicked — without this, a button that reads pressed=true at rest
  // (some devices report a trigger/analog button as "pressed" past a small
  // threshold even untouched) would be picked immediately as the "newly
  // pressed" button on every single Bind click, making it impossible to
  // ever bind anything else. Only a transition from not-pressed to pressed
  // counts.
  private buttonBaseline: boolean[][] = [];

  /** Which device (pad index) the live-inputs panel currently shows. */
  private selectedPad = -1;
  private lastDeviceKey = '';

  private axisBarEls: HTMLDivElement[] = [];
  private buttonEls: HTMLDivElement[] = [];
  private statusEl!: HTMLDivElement;
  private bodyEl!: HTMLDivElement;
  private devSelEl!: HTMLSelectElement;
  private sourceEl!: HTMLDivElement;
  private hidStatusEl!: HTMLSpanElement;

  constructor(private gamepad: Gamepad) {
    this.injectStyles();
    this.root = document.createElement('div');
    this.root.id = 'joyCfg';
    this.root.style.display = 'none';
    document.body.appendChild(this.root);
    this.build();
  }

  toggle(): void {
    this.open ? this.close() : this.show();
  }

  show(): void {
    this.open = true;
    this.gamepad.suspended = true;
    this.root.style.display = 'flex';
    this.lastDeviceKey = '';
    this.render();
    const loop = () => {
      if (!this.open) return;
      this.tick();
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
  }

  close(): void {
    this.open = false;
    this.listening = null;
    cancelAnimationFrame(this.raf);
    saveConfig(this.gamepad.config);
    this.gamepad.reloadConfig();
    this.gamepad.suspended = false;
    this.root.style.display = 'none';
  }

  // ---- live update loop ----------------------------------------------------
  private tick(): void {
    const pads = this.gamepad.getPads();
    const devices = indexedPads(pads);

    // Rebuild the device list/selector only when the set of connected
    // devices actually changes (hot-plug), not every frame.
    const key = devices.map((d) => d.index).join(',');
    if (key !== this.lastDeviceKey) {
      this.lastDeviceKey = key;
      if (!devices.some((d) => d.index === this.selectedPad)) {
        this.selectedPad = devices[0]?.index ?? -1;
      }
      this.render();
    }

    // Live axes/buttons for the selected device only.
    const selected = pads[this.selectedPad];
    if (selected) {
      for (let i = 0; i < this.axisBarEls.length; i++) {
        const v = i < selected.axes.length ? selected.axes[i] : 0;
        this.axisBarEls[i].style.left = `${50 + v * 50 * 0.98}%`;
      }
      for (let i = 0; i < this.buttonEls.length; i++) {
        const pressed = selected.buttons[i]?.pressed ?? false;
        this.buttonEls[i].classList.toggle('jc-btn-on', pressed);
      }
    }

    // Capture bindings — scan every connected device, not just the selected one.
    if (this.listening) {
      if (this.listening.kind === 'axis') {
        let best = -1, bestPad = -1, bestDelta = 0.5;
        for (const { index, pad } of devices) {
          const baseline = this.axisBaseline[index] ?? [];
          for (let i = 0; i < pad.axes.length; i++) {
            const d = Math.abs(pad.axes[i] - (baseline[i] ?? 0));
            if (d > bestDelta) { bestDelta = d; best = i; bestPad = index; }
          }
        }
        if (best >= 0) {
          const ctrl = this.listening.ctrl;
          const cur = this.gamepad.config[ctrl] as AxisBinding | null;
          this.gamepad.config[ctrl] = { pad: bestPad, axis: best, invert: cur?.invert ?? false };
          this.listening = null;
          this.render();
        }
      } else {
        outer: for (const { index, pad } of devices) {
          const baseline = this.buttonBaseline[index] ?? [];
          const idx = pad.buttons.findIndex((b, i) => b.pressed && !baseline[i]);
          if (idx >= 0) {
            this.gamepad.config.buttons[this.listening.ctrl] = { pad: index, button: idx };
            this.listening = null;
            this.render();
            break outer;
          }
        }
      }
    }
    this.updateStatus(devices.length);
  }

  private updateStatus(n: number): void {
    if (this.listening) {
      this.statusEl.textContent =
        this.listening.kind === 'axis'
          ? `Move the axis for “${this.listening.ctrl}” (on any connected device)…`
          : `Press the button for “${this.listening.ctrl}” (on any connected device)…`;
      this.statusEl.style.color = '#ffd23f';
    } else if (n === 0) {
      this.statusEl.textContent = 'No joystick detected — connect one and press a button.';
      this.statusEl.style.color = '#e88';
    } else {
      this.statusEl.textContent = `${n} device${n === 1 ? '' : 's'} connected.`;
      this.statusEl.style.color = '#8fb';
    }
  }

  // ---- rendering -----------------------------------------------------------
  private build(): void {
    const card = document.createElement('div');
    card.className = 'jc-card';
    card.innerHTML = `
      <h2>Joystick Configuration</h2>
      <div class="jc-source"></div>
      <div class="jc-status"></div>
      <div class="jc-devices"></div>
      <div class="jc-body"></div>
      <div class="jc-actions">
        <button class="jc-reset">Reset to defaults</button>
        <button class="jc-close">Save &amp; Close (Ctrl+J)</button>
      </div>`;
    this.root.appendChild(card);
    this.sourceEl = card.querySelector('.jc-source') as HTMLDivElement;
    this.statusEl = card.querySelector('.jc-status') as HTMLDivElement;
    this.bodyEl = card.querySelector('.jc-body') as HTMLDivElement;
    (card.querySelector('.jc-close') as HTMLButtonElement).onclick = () => this.close();
    (card.querySelector('.jc-reset') as HTMLButtonElement).onclick = () => {
      this.gamepad.config = defaultConfig();
      this.render();
    };
    // Clicking the dimmed backdrop closes too.
    this.root.addEventListener('mousedown', (e) => { if (e.target === this.root) this.close(); });
    this.buildSourcePicker();
  }

  /** Input-source picker: Gamepad API (default) vs WebHID (no ~4-device cap,
   * but each device needs an explicit one-time grant via the browser's
   * device picker — see WebHidInput's doc comment for why). */
  private buildSourcePicker(): void {
    this.sourceEl.innerHTML = `
      <div class="jc-src-row">
        <label class="jc-src-opt"><input type="radio" name="jc-src" value="gamepadapi"/> Gamepad API</label>
        <label class="jc-src-opt"><input type="radio" name="jc-src" value="webhid"/> WebHID (no device-count cap)</label>
        <button class="jc-hid-add">Add HID device…</button>
        <span class="jc-hid-status"></span>
      </div>
      <p class="jc-hint">The Gamepad API caps out around 4 simultaneous controllers on
      Chrome/Windows (a browser/OS limitation, not this app) — switch to WebHID if you
      have more than that connected. WebHID needs each device granted individually.</p>`;
    this.hidStatusEl = this.sourceEl.querySelector('.jc-hid-status') as HTMLSpanElement;
    this.sourceEl.querySelectorAll<HTMLInputElement>('input[name="jc-src"]').forEach((el) => {
      el.addEventListener('change', () => {
        if (el.checked) {
          this.gamepad.setMode(el.value as InputMode);
          this.selectedPad = -1;
          this.lastDeviceKey = '';
          this.render();
        }
      });
    });
    const addBtn = this.sourceEl.querySelector('.jc-hid-add') as HTMLButtonElement;
    addBtn.addEventListener('click', () => {
      this.hidStatusEl.textContent = 'Waiting for device picker…';
      this.hidStatusEl.style.color = '#ffd23f';
      this.gamepad.requestHidDevice()
        .then(() => {
          this.hidStatusEl.textContent = 'Device added.';
          this.hidStatusEl.style.color = '#8fb';
          this.render();
        })
        .catch((err: unknown) => {
          this.hidStatusEl.textContent = err instanceof Error ? err.message : 'Could not add device.';
          this.hidStatusEl.style.color = '#e88';
        });
    });
  }

  private render(): void {
    const cfg = this.gamepad.config;
    const pads = this.gamepad.getPads();
    const devices = indexedPads(pads);

    this.sourceEl.querySelectorAll<HTMLInputElement>('input[name="jc-src"]').forEach((el) => {
      el.checked = el.value === this.gamepad.mode;
    });
    const addBtn = this.sourceEl.querySelector('.jc-hid-add') as HTMLButtonElement;
    if (this.gamepad.mode !== 'webhid') {
      addBtn.disabled = true;
      addBtn.title = 'Switch to WebHID above first';
    } else if (!this.gamepad.hid.supported) {
      addBtn.disabled = true;
      addBtn.title = 'WebHID is not supported in this browser (try a recent Chrome/Edge)';
      this.hidStatusEl.textContent = 'WebHID unsupported in this browser.';
      this.hidStatusEl.style.color = '#e88';
    } else {
      addBtn.disabled = false;
      addBtn.title = '';
    }
    if (!devices.some((d) => d.index === this.selectedPad)) {
      this.selectedPad = devices[0]?.index ?? -1;
    }

    const devicesEl = this.root.querySelector('.jc-devices') as HTMLDivElement;
    devicesEl.innerHTML = devices.length === 0
      ? '<p class="jc-hint">No devices connected.</p>'
      : devices.map(({ index, pad }) =>
        `<div class="jc-device"><b>P${index}</b> ${escapeHtml(pad.id)} <span class="jc-muted">(${pad.axes.length} axes, ${pad.buttons.length} buttons)</span></div>`,
      ).join('');

    const fmtAxis = (bind: AxisBinding | null) => (bind ? `P${bind.pad} axis ${bind.axis}` : 'none');
    const fmtBtn = (bind: ButtonBinding | null) => (bind ? `P${bind.pad} btn ${bind.button}` : 'none');

    const axisRows = AXIS_CTRLS.map(({ key, label }) => {
      const bind = cfg[key] as AxisBinding | null;
      const inv = bind?.invert ? 'checked' : '';
      const invDisabled = bind ? '' : 'disabled';
      return `<div class="jc-row">
        <span class="jc-lbl">${label}</span>
        <span class="jc-cur">${fmtAxis(bind)}</span>
        <label class="jc-inv"><input type="checkbox" data-inv="${key}" ${inv} ${invDisabled}/> invert</label>
        <button data-bind-axis="${key}">Bind</button>
        ${NULLABLE_AXIS_CTRLS.has(key) ? `<button data-none-axis="${key}">None</button>` : ''}
      </div>`;
    }).join('');

    const btnRows = BTN_CTRLS.map(({ key, label }) => {
      const bind = cfg.buttons[key];
      return `<div class="jc-row">
        <span class="jc-lbl">${label}</span>
        <span class="jc-cur">${fmtBtn(bind)}</span>
        <button data-bind-btn="${key}">Bind</button>
        <button data-none-btn="${key}">None</button>
      </div>`;
    }).join('');

    const devOptions = devices.map(({ index, pad }) =>
      `<option value="${index}" ${index === this.selectedPad ? 'selected' : ''}>P${index} — ${escapeHtml(pad.id)}</option>`,
    ).join('');
    const selectedDevice = devices.find((d) => d.index === this.selectedPad) ?? null;
    const liveAxes = selectedDevice
      ? Array.from({ length: selectedDevice.pad.axes.length }, (_, i) =>
        `<div class="jc-axline"><span>axis ${i}</span><div class="jc-track"><div class="jc-fill"></div></div></div>`,
      ).join('') || '<p class="jc-hint">No axes on this device.</p>'
      : '<p class="jc-hint">No device selected.</p>';
    const liveButtons = selectedDevice
      ? Array.from({ length: selectedDevice.pad.buttons.length }, (_, i) =>
        `<div class="jc-btn" data-btn-i="${i}">${i}</div>`,
      ).join('') || '<p class="jc-hint">No buttons on this device.</p>'
      : '';

    this.bodyEl.innerHTML = `
      <div class="jc-col">
        <h3>Axes</h3>${axisRows}
        <div class="jc-dz">
          <label>Deadzone <b class="jc-dzval">${cfg.deadzone.toFixed(2)}</b></label>
          <input type="range" min="0" max="0.3" step="0.01" value="${cfg.deadzone}" class="jc-dzslider"/>
        </div>
      </div>
      <div class="jc-col">
        <h3>Buttons</h3>${btnRows}
        <p class="jc-hint">Click “Bind”, then move the axis or press the button on
        whichever connected device you want — every control can come from a
        different device. Changes save when you close.</p>
      </div>
      <div class="jc-col jc-col-wide">
        <h3>Live inputs</h3>
        <select class="jc-devsel">${devOptions || '<option>none</option>'}</select>
        <div class="jc-liveaxes">${liveAxes}</div>
        <div class="jc-livebuttons">${liveButtons}</div>
      </div>`;

    this.axisBarEls = Array.from(this.bodyEl.querySelectorAll('.jc-fill'));
    this.buttonEls = Array.from(this.bodyEl.querySelectorAll('.jc-btn'));

    this.devSelEl = this.bodyEl.querySelector('.jc-devsel') as HTMLSelectElement;
    this.devSelEl.addEventListener('change', () => {
      this.selectedPad = parseInt(this.devSelEl.value, 10);
      this.render();
    });

    this.bodyEl.querySelectorAll('[data-bind-axis]').forEach((el) =>
      (el as HTMLButtonElement).addEventListener('click', () => {
        const ctrl = (el as HTMLElement).dataset.bindAxis as AxisCtrl;
        this.axisBaseline = pads.map((p) => (p ? Array.from(p.axes) : []));
        this.listening = { kind: 'axis', ctrl };
      }),
    );
    this.bodyEl.querySelectorAll('[data-none-axis]').forEach((el) =>
      (el as HTMLButtonElement).addEventListener('click', () => {
        const ctrl = (el as HTMLElement).dataset.noneAxis as AxisCtrl;
        if (NULLABLE_AXIS_CTRLS.has(ctrl)) (cfg as Record<AxisCtrl, AxisBinding | null>)[ctrl] = null;
        this.render();
      }),
    );
    this.bodyEl.querySelectorAll('[data-bind-btn]').forEach((el) =>
      (el as HTMLButtonElement).addEventListener('click', () => {
        this.buttonBaseline = pads.map((p) => (p ? p.buttons.map((b) => b.pressed) : []));
        this.listening = { kind: 'button', ctrl: (el as HTMLElement).dataset.bindBtn as BtnCtrl };
      }),
    );
    this.bodyEl.querySelectorAll('[data-none-btn]').forEach((el) =>
      (el as HTMLButtonElement).addEventListener('click', () => {
        cfg.buttons[(el as HTMLElement).dataset.noneBtn as BtnCtrl] = null;
        this.render();
      }),
    );
    this.bodyEl.querySelectorAll('[data-inv]').forEach((el) =>
      (el as HTMLInputElement).addEventListener('change', () => {
        const ctrl = (el as HTMLElement).dataset.inv as AxisCtrl;
        const bind = cfg[ctrl] as AxisBinding | null;
        if (bind) bind.invert = (el as HTMLInputElement).checked;
      }),
    );
    const dz = this.bodyEl.querySelector('.jc-dzslider') as HTMLInputElement;
    const dzVal = this.bodyEl.querySelector('.jc-dzval') as HTMLElement;
    dz.addEventListener('input', () => {
      cfg.deadzone = parseFloat(dz.value);
      dzVal.textContent = cfg.deadzone.toFixed(2);
    });
  }

  private injectStyles(): void {
    const s = document.createElement('style');
    s.textContent = `
      #joyCfg { position: fixed; inset: 0; z-index: 20; display: flex;
        align-items: center; justify-content: center; background: rgba(0,0,0,0.6);
        font-family: system-ui, sans-serif; }
      #joyCfg .jc-card { background: #14181c; color: #dfe6ea; border: 1px solid #2b3339;
        border-radius: 10px; padding: 20px 24px; width: min(980px, 96vw);
        max-height: 92vh; overflow: auto; box-shadow: 0 10px 40px rgba(0,0,0,0.6); }
      #joyCfg h2 { margin: 0 0 6px; font-size: 18px; letter-spacing: 1px; }
      #joyCfg h3 { margin: 14px 0 8px; font-size: 13px; color: #8fb; text-transform: uppercase; letter-spacing: 1px; }
      #joyCfg .jc-status { font: 12px ui-monospace, monospace; min-height: 16px; margin-bottom: 8px; }
      #joyCfg .jc-source { margin-bottom: 10px; padding-bottom: 10px; border-bottom: 1px solid #2b3339; }
      #joyCfg .jc-src-row { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; font-size: 12px; }
      #joyCfg .jc-src-opt { display: flex; align-items: center; gap: 4px; cursor: pointer; }
      #joyCfg .jc-hid-status { font: 11px ui-monospace, monospace; }
      #joyCfg .jc-devices { font: 12px ui-monospace, monospace; margin-bottom: 10px; }
      #joyCfg .jc-device { padding: 3px 0; color: #cfe; }
      #joyCfg .jc-muted { color: #789; }
      #joyCfg .jc-body { display: grid; grid-template-columns: 1fr 1fr 1.2fr; gap: 20px; }
      @media (max-width: 820px) { #joyCfg .jc-body { grid-template-columns: 1fr; } }
      #joyCfg .jc-row { display: flex; align-items: center; gap: 8px; margin: 5px 0; font-size: 13px; }
      #joyCfg .jc-lbl { flex: 1; }
      #joyCfg .jc-cur { width: 90px; color: #9fe; font: 12px ui-monospace, monospace; }
      #joyCfg .jc-inv { font-size: 11px; color: #9aa; display: flex; align-items: center; gap: 3px; }
      #joyCfg button, #joyCfg select { background: #223; color: #cfe; border: 1px solid #345; border-radius: 5px;
        padding: 4px 10px; cursor: pointer; font-size: 12px; }
      #joyCfg button:hover { background: #2b3f52; }
      #joyCfg button:disabled { opacity: 0.4; cursor: not-allowed; }
      #joyCfg button:disabled:hover { background: #223; }
      #joyCfg .jc-devsel { width: 100%; margin-bottom: 10px; font: 12px ui-monospace, monospace; }
      #joyCfg .jc-actions { display: flex; justify-content: space-between; margin-top: 18px; }
      #joyCfg .jc-close { background: #1d6; color: #041; border: none; font-weight: 700; }
      #joyCfg .jc-close:hover { background: #2f7; }
      #joyCfg .jc-axline { display: flex; align-items: center; gap: 8px; margin: 3px 0; font: 11px ui-monospace, monospace; }
      #joyCfg .jc-axline span { width: 46px; color: #9aa; }
      #joyCfg .jc-track { position: relative; flex: 1; height: 10px; background: #0c1013; border: 1px solid #2b3339; border-radius: 5px; }
      #joyCfg .jc-fill { position: absolute; top: -2px; width: 6px; height: 12px; background: #3fd06a; border-radius: 2px; transform: translateX(-50%); }
      #joyCfg .jc-livebuttons { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 10px; }
      #joyCfg .jc-btn { width: 26px; height: 22px; display: flex; align-items: center; justify-content: center;
        background: #0c1013; border: 1px solid #2b3339; border-radius: 4px; font: 11px ui-monospace, monospace; color: #789; }
      #joyCfg .jc-btn-on { background: #3fd06a; border-color: #3fd06a; color: #052; font-weight: 700; }
      #joyCfg .jc-dz { margin: 10px 0; font-size: 12px; }
      #joyCfg .jc-dz input { width: 100%; }
      #joyCfg .jc-hint { font-size: 11px; color: #889; line-height: 1.5; margin-top: 14px; }
    `;
    document.head.appendChild(s);
  }
}

/** All connected (non-null) pads, paired with their original slot index. */
function indexedPads(pads: (MinimalPad | null)[]): { index: number; pad: MinimalPad }[] {
  const out: { index: number; pad: MinimalPad }[] = [];
  pads.forEach((p, i) => { if (p) out.push({ index: i, pad: p }); });
  return out;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}
